import React, {useEffect, useState} from 'react';
import styles from "./VaultAdministration.module.scss";
import { DataGrid } from '@mui/x-data-grid';
import {calculateBlockchain, hasValue} from "../../helpers/helperFunctions";
import LoadingSpinner from "../../Components/LoadingSpinner/LoadingSpinner";
import TextInput from "../../Components/TextInput/TextInput";
import Button, {BUTTON_TYPE_SECONDARY} from "../../Components/Button/Button";
import {getApiUrl} from "../../helpers/helper";
import TabSelector from "../../Components/TabSelector/TabSelector";
import {
  getVaultProgramData,
  getVaultState,
  updateAprRates,
  updateHydrazineExchangeRates
} from "../../helpers/vaultHelper";
import moment from "moment";
import {PROD_API_URL} from "../../constants/constants";
import { saveAs } from "file-saver";
import Papa from "papaparse";
import VaultChart from "./VaultChart";

const TAB_VAULT_LIST = 'TAB_VAULT_LIST';
const TAB_STAKER_LIST = 'TAB_STAKER_LIST';
const TAB_SETTINGS = 'TAB_SETTINGS';
const TAB_CHART_OVERVIEW = 'TAB_CHART_OVERVIEW';

const tabOptions = [
  {key: TAB_VAULT_LIST, label: 'Vault List'},
  {key: TAB_STAKER_LIST, label: 'Staker List'},
  {key: TAB_SETTINGS, label: 'Settings'},
  {key: TAB_CHART_OVERVIEW, label: 'Chart Overview'},
];

const columns = [
  { field: 'solanaWallet', headerName: 'Wallet', flex: 2 },
  { field: 'amount', headerName: 'Staked', width: 200 },
  { field: 'lockOption', headerName: 'Lock Option', width: 150 },
  { field: 'boxName', headerName: 'Name', flex: 1 },
  { field: 'createdAtFormatted', headerName: 'Created At', width: 200 },
  { field: 'expiresTimestampFormatted', headerName: 'Expires', width: 200 },
];

const VaultAdministration = ({ provider }) => {
  const [rows, setRows] = useState();
  const [chartData, setChartData] = useState();
  const [loading, setLoading] = useState();
  const [selectedRow, setSelectedRow] = useState();
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [searchWalletText, setSearchWalletText] = useState();

  const [tab, setTab] = useState(TAB_VAULT_LIST);

  const [vaultPenalty, setVaultPenalty] = useState();
  const [hydrazineExchangeRate, setHydrazineExchangeRate] = useState({ year: 0, halfYear: 0, quarterYear: 0, exchangeRate: 0 });
  const [vaultContractData, setVaultContractData] = useState();

  const [hydrazineConversion, setHydrazineConversion] = useState(0);
  const [hydrazineConversionAmount, setHydrazineConversionAmount] = useState(0);

  const api = getApiUrl();


  useEffect( ()=>{
    const fetchVaultsData = async () => {
      setLoading(true);
      await fetch(PROD_API_URL + `/vaults`, {
        "method": "GET",
        "headers": {"content-type": "application/json", "accept": "*"},
      }).then(res=>res.json())
        .then((response)=>{
          const formattedRows = formatRows(response?.data.result);
          console.log(formattedRows)
          setRows(formattedRows);
          setTotal(response?.data?.total)

        });

      setLoading(false);
    }

    fetchVaultsData();
  },[]);

  useEffect(() => {
    if (provider?.wallet?.publicKey && !vaultContractData) {
      fetchVaultContractData();
    }
  }, [provider?.wallet?.publicKey]);

  const fetchVaultContractData = async () => {
    const nVaultState = await getVaultState({provider});

    setVaultPenalty(nVaultState.penaltyRate.toNumber())

    const exchangeRate = {
      year: nVaultState.exchangeRateYear.toNumber() / 10000,
      halfYear: nVaultState.exchangeRateHalfYear.toNumber() / 10000,
      quarterYear: nVaultState.exchangeRateQuarterYear.toNumber() / 10000,
      exchangeRate: nVaultState.exchangeRate.toNumber() / 10000,
    }
    setHydrazineExchangeRate(exchangeRate);

    const vaultData = {
      quarterYearApr: nVaultState.quarterYearApr.toNumber(),
      halfYearApr: nVaultState.halfYearApr.toNumber(),
      yearLockApr: nVaultState.yearLockApr.toNumber(),
      totalHydrazineBurned: (nVaultState.totalHydrazineBurned.toNumber()).toLocaleString(),
      totalStarsStaked: (nVaultState.totalStarsStaked.toNumber()).toLocaleString(),
    }

    setVaultContractData(vaultData);
  }

  const onSearch = async () => {
    setLoading(true);

    await fetch(api + `/search?wallet=${searchWalletText}`)
      .then(res=>res.json())
      .then((response)=>{
        if(response?.status){
          const formattedRows = formatRows(response?.data);
          setRows(formattedRows);
          setTotal(1)
        }
      })
      .catch(function (error) {
        console.error(error);
      });


    setLoading(false);
  }

  const formatRows = (data) => {
    let hydrazineConversion = 0;
    let hydrazineConversionAmount = 0;
    let totalVaults = 0;
    let totalStaked = 0;

    const chartData = new Map();

    const result = data.map(row => {
      if (row?.isConvertedHydrazineVault) {
        hydrazineConversion = hydrazineConversion + 1;
        hydrazineConversionAmount = hydrazineConversionAmount + Number(row.amount);
      }
      totalVaults = totalVaults + 1;
      totalStaked = totalStaked + Number(row.amount);
      const date = moment.unix(row.createdAt / 1000).format('YYYY-MM-DD');
      const value = { vaults: (chartData.get(date)?.vaults || 0) + 1, '90Day': (chartData.get(date)?.['90Day'] || 0), '180Day': (chartData.get(date)?.['180Day'] || 0), '360Day': (chartData.get(date)?.['360Day'] || 0), 'stakedAmount': (chartData.get(date)?.['stakedAmount'] || 0) + Number(row.amount) };
      if (row.lock.value === 1) {
        value['360Day'] = (chartData.get(date)?.['360Day'] || 0) + 1;
      }
      if (row.lock.value === 2) {
        value['180Day'] = (chartData.get(date)?.['180Day'] || 0) + 1;
      }
      if (row.lock.value === 3) {
        value['90Day'] = (chartData.get(date)?.['90Day'] || 0) + 1;
      }

      chartData.set(date, value);
      return {
        id: row.id,
        lockOption: row?.lock?.label,
        createdAtFormatted: moment.unix(row.createdAt / 1000).format('YYYY-MM-DD, h:mm A'),
        ...row,
      }
    })

    const chartDataArray = Array.from(chartData, ([key, value]) => {
      totalVaults = totalVaults - value.vaults;
      totalStaked = totalStaked - value.stakedAmount;
      return (
          {
            date: key,
            "Total Created Vaults": totalVaults,
            "Total Staked Amount": totalStaked,
            "Vaults Per Day": value.vaults,
            '90Day': value['90Day'],
            '180Day': value['180Day'],
            '360Day': value['360Day'],
            'Staked Amount Per Day': Math.round(value['stakedAmount'])
          }
      )
    });
    const chartDataFinal = chartDataArray.reverse();

    setChartData(chartDataFinal);

    setHydrazineConversion(hydrazineConversion);
    setHydrazineConversionAmount(hydrazineConversionAmount);

    return result;
  }

  const downloadCSV = async () => {
    const csv = Papa.unparse(rows);
    const filename = `vaults_${moment().format('YYYY-MM-DD')}.csv`;
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    saveAs(blob, filename);
  }

  const updateHydrazineSettings = async () => {
    await updateHydrazineExchangeRates({ provider, exchangeRates: hydrazineExchangeRate, hydrazineAprs: vaultContractData  });
  }

  const updateVaultAprRates = async () => {
    await updateAprRates({ provider, aprs: { yearLockApr: 2400, halfYearApr: 1600, quarterYearApr: 800  } });
  }

  return (
    <main id="home" key="home" className={styles.mainIdoContainer}>
       <div className={styles.idoAdminContainer}>
          <div className={styles.stakerHeaderContainer}>
            <TabSelector
                options={tabOptions}
                setValue={setTab}
                tabContainerClassName={styles.tabSelector}
                value={tab} />
            {tab !== TAB_SETTINGS && tab !== TAB_STAKER_LIST && <Button text="Download CSV"  buttonType={BUTTON_TYPE_SECONDARY} onClick={downloadCSV}/>}
          </div>

           {tab === TAB_SETTINGS && <div className={styles.settingsContainer}>
             <div className={styles.settingsRow}>
               <TextInput value={vaultPenalty} label="Penalty Rate" setValue={setVaultPenalty}/>
               <Button text="Update Penalty Rate"  buttonType={BUTTON_TYPE_SECONDARY}/>
             </div>
             <div className={styles.settingsRow}>
               <TextInput value={hydrazineExchangeRate.exchangeRate} label="Hydrazine Exchange Rate"
                          setValue={(value) => setHydrazineExchangeRate((prev) => ({...prev, exchangeRate: value}))}/>
               <TextInput value={hydrazineExchangeRate.year} label="Hydrazine Exchange Rate Year"
                          setValue={(value) => setHydrazineExchangeRate((prev) => ({...prev, year: value}))}/>
               <TextInput value={hydrazineExchangeRate.halfYear} label="Hydrazine Exchange Rate Half Year"
                          setValue={(value) => setHydrazineExchangeRate((prev) => ({...prev, halfYear: value}))}/>
               <TextInput value={hydrazineExchangeRate.quarterYear} label="Hydrazine Exchange Rate Quarter Year"
                          setValue={(value) => setHydrazineExchangeRate((prev) => ({...prev, quarterYear: value}))}/>
             </div>
             <div className={styles.settingsRow}>
               <div className={styles.valueContainer}>
                 <span className={styles.valueLabel}>Total Stars Staked: </span>
                 <span className={styles.value}>{vaultContractData?.totalStarsStaked}</span>
               </div>
               <div className={styles.valueContainer}>
                 <span className={styles.valueLabel}>Total Hydrazine Burned: </span>
                 <span className={styles.value}>{vaultContractData?.totalHydrazineBurned}</span>
               </div>
             </div>
             <div className={styles.settingsRow}>
               <div className={styles.valueContainer}>
                 <span className={styles.valueLabel}>Curren Year APR: </span>
                 <span className={styles.value}>{vaultContractData?.yearLockApr}</span>
               </div>
               <div className={styles.valueContainer}>
                 <span className={styles.valueLabel}>Current Half Year APR: </span>
                 <span className={styles.value}>{vaultContractData?.halfYearApr}</span>
               </div>
               <div className={styles.valueContainer}>
                 <span className={styles.valueLabel}>Current Quarter Year APR: </span>
                 <span className={styles.value}>{vaultContractData?.quarterYearApr}</span>
               </div>
             </div>
             <Button text="Update Hydrazine Settings"  buttonType={BUTTON_TYPE_SECONDARY} onClick={updateHydrazineSettings}/>
             <Button text="Update APR Rates"  buttonType={BUTTON_TYPE_SECONDARY} onClick={updateVaultAprRates}/>
           </div>}
         {tab === TAB_VAULT_LIST && <div className={styles.stakerContainer}>
             {loading && <LoadingSpinner local/>}
              <div className={styles.vaultHeaderContainer}>
                <span className={styles.value}>Total Number of Hydrazine Converted Vaults: {hydrazineConversion}</span>
                <span className={styles.value}>Hydrazine Converted Vaults Staked: {Math.round(hydrazineConversionAmount).toLocaleString()}</span>
              </div>
               <div className={styles.tableContainer}>

                   {rows &&
                       <DataGrid
                           rows={rows}
                           columns={columns}
                       />

                   }
               </div>
           </div>}
         {tab === TAB_CHART_OVERVIEW && <VaultChart data={chartData} />}
         {tab === TAB_STAKER_LIST && <StakerList />}
       </div>
    </main>

  );
}

export default VaultAdministration;

const StakerColums = [
  { field: 'solanaWallet', headerName: 'Wallet', flex: 1 },
  { field: 'totalAmount', headerName: 'Total Staked', width: 250 },
  { field: 'vaultsCount', headerName: 'No. of Vaults', width: 150 },
  { field: '90DayLockAmount', headerName: '90 Day Amount', width: 150 },
  { field: '180DayLockAmount', headerName: '180 Day Amount', width: 150 },
  { field: '360DayLockAmount', headerName: '360 Day Amount', width: 150 },
];

const StakerList = () => {
  const [rows, setRows] = useState();
  const [loading, setLoading] = useState();

  useEffect( ()=>{
    const fetchSaleData = async () => {
      setLoading(true);
      await fetch(PROD_API_URL + `/vaults/stakers`, {
        "method": "GET",
        "headers": {"content-type": "application/json", "accept": "*"},
      }).then(res=>res.json())
          .then((response)=>{
            const formattedRows = formatRows(response?.data);
            console.log(formattedRows)
            setRows(formattedRows);

          });

      setLoading(false);
    }

    fetchSaleData();
  },[]);


  const formatRows = (data) => {
    return data.map(row => {
      return {
        id: row.solanaWallet,
        totalAmount: row.amount,
        '90DayLockAmount': row.lockAmount3,
        '180DayLockAmount': row.lockAmount2,
        '360DayLockAmount': row.lockAmount1,
        ...row,
      }
    })
  }

  const downloadCSV = async () => {
    const csv = Papa.unparse(rows);
    const filename = `vaults_${moment().format('YYYY-MM-DD')}.csv`;
    const blob = new Blob(["\uFEFF" + csv], { type: "text/csv;charset=utf-8" });
    saveAs(blob, filename);
  }



  return (
    <div className={styles.stakerContainer}>
      {loading && <LoadingSpinner local/>}
        <div className={styles.tableContainer}>
          <Button text="Download CSV"  buttonType={BUTTON_TYPE_SECONDARY} onClick={downloadCSV}/>
        {rows &&
        <DataGrid
            rows={rows}
            columns={StakerColums}
        />}
      </div>
    </div>
  )
}
